import { SaveOutlined } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById, updateData } from '../../../MicrospecDynamicUI/Helpers/dataUtils';

const AVAILABLE_ROLES = [
  { value: 'normal', label: 'مستخدم عادي (normal)' },
  { value: 'admin', label: 'مدير النظام (admin)' },
];

// ─── Section Wrapper ──────────────────────────────────────────────────────────
const FormSection = ({ title, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AddUser = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;

  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!firestore?.instance || !userId) return;

    getDocById(
      (doc) => {
        if (!doc) return;
        setUser(doc);
        setRoles(doc.roles || []);
      },
      (done) => { if (done) setLoading(false); },
      firestore.instance,
      'users',
      userId,
      false
    );
  }, [firestore?.instance, userId]);

  const handleRoleToggle = (role) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateData(`users/${userId}`, firestore, { roles });
      setSnackbar({ open: true, message: 'تم تحديث صلاحيات المستخدم بنجاح', severity: 'success' });
      setTimeout(() => navigate('/admin/users/manage'), 1200);
    } catch (err) {
      console.error('Error updating user roles:', err);
      setSnackbar({ open: true, message: 'حدث خطأ أثناء الحفظ، يرجى المحاولة مرة أخرى', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">لم يتم العثور على المستخدم</Alert>
      </Container>
    );
  }

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* Header with avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar
          src={user.photo_url || ''}
          sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.25rem' }}
        >
          {!user.photo_url && initials}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {fullName || 'مستخدم'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            تعديل بيانات المستخدم
          </Typography>
        </Box>
      </Box>

      {/* Basic Info — all disabled */}
      <FormSection title="المعلومات الأساسية">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField
            label="الاسم الأول"
            value={user.first_name || ''}
            disabled
            fullWidth
          />
          <TextField
            label="اسم العائلة"
            value={user.last_name || ''}
            disabled
            fullWidth
          />
        </Box>
        <TextField
          label="البريد الإلكتروني"
          value={user.email || ''}
          disabled
          fullWidth
          sx={{ mb: 2 }}
        />
        {user.phone && (
          <TextField
            label="رقم الهاتف"
            value={user.phone}
            disabled
            fullWidth
            inputProps={{ dir: 'ltr' }}
            sx={{ mb: 2 }}
          />
        )}
      </FormSection>

      {/* Additional profile fields — all disabled */}
      {(user.address || user.yearsOfExperience != null || user.about) && (
        <FormSection title="بيانات إضافية">
          {user.address && (
            <TextField
              label="العنوان"
              value={user.address}
              disabled
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          {user.yearsOfExperience != null && (
            <TextField
              label="سنوات الخبرة"
              value={user.yearsOfExperience}
              disabled
              fullWidth
              type="number"
              sx={{ mb: 2 }}
            />
          )}
          {user.about && (
            <TextField
              label="نبذة عن المستخدم"
              value={user.about}
              disabled
              fullWidth
              multiline
              rows={3}
            />
          )}
        </FormSection>
      )}

      {/* Roles — editable */}
      <FormSection title="الصلاحيات">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          حدد الأدوار التي تريد تعيينها لهذا المستخدم
        </Typography>
        <FormGroup>
          {AVAILABLE_ROLES.map((role) => (
            <FormControlLabel
              key={role.value}
              control={
                <Checkbox
                  checked={roles.includes(role.value)}
                  onChange={() => handleRoleToggle(role.value)}
                  color="primary"
                />
              }
              label={role.label}
            />
          ))}
        </FormGroup>
      </FormSection>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/users/manage')}
          disabled={saving}
        >
          إلغاء
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
          onClick={handleSave}
          disabled={saving}
        >
          حفظ الصلاحيات
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ position: 'fixed', bottom: 24 }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddUser;
